import { Button, Select, SelectItem, Text } from '@tremor/react';
import { fieldLabel } from '../lib/format';
import type { ReportSortSpec } from '../api/types';

interface Props {
  /** Columns the user can sort on — same list ColumnPicker shows. */
  available: string[];
  value: ReportSortSpec[];
  onChange: (next: ReportSortSpec[]) => void;
}

/**
 * Visual multi-key sort editor. Each row is `{col, dir}`; the SPA sorts
 * top-to-bottom (handler's compareAny applies them in array order).
 * Replaces the freeform "subt_mxn:desc,name:asc" text input — the old
 * syntax was hostile to non-developers and silently dropped typos.
 */
export default function SortEditor({ available, value, onChange }: Props) {
  function add() {
    // Default new row to the first column that isn't already sorted on,
    // ascending. Operator can flip with one click.
    const used = new Set(value.map((s) => s.col));
    const first = available.find((c) => !used.has(c)) ?? available[0] ?? '';
    onChange([...value, { col: first, dir: 'asc' }]);
  }
  function update(i: number, patch: Partial<ReportSortSpec>) {
    const next = value.slice();
    next[i] = { ...next[i], ...patch };
    onChange(next);
  }
  function remove(i: number) {
    onChange(value.filter((_, idx) => idx !== i));
  }
  function move(i: number, delta: number) {
    const target = i + delta;
    if (target < 0 || target >= value.length) return;
    const next = value.slice();
    [next[i], next[target]] = [next[target], next[i]];
    onChange(next);
  }

  return (
    <div className="space-y-2">
      {value.length === 0 && (
        <Text className="text-tremor-content text-xs italic">
          Sin ordenamiento — los resultados llegan en el orden del daemon.
        </Text>
      )}
      {value.map((spec, i) => (
        <div key={i} className="flex items-center gap-2">
          <Text className="text-tremor-content text-xs w-4 text-right">{i + 1}.</Text>
          <div className="flex-1">
            <Select value={spec.col} onValueChange={(v) => update(i, { col: v })}>
              {available.map((col) => (
                <SelectItem key={col} value={col}>{fieldLabel(col)}</SelectItem>
              ))}
            </Select>
          </div>
          <div className="w-36">
            <Select value={spec.dir} onValueChange={(v) => update(i, { dir: v as 'asc' | 'desc' })}>
              <SelectItem value="asc">Ascendente</SelectItem>
              <SelectItem value="desc">Descendente</SelectItem>
            </Select>
          </div>
          <button
            type="button"
            onClick={() => move(i, -1)}
            disabled={i === 0}
            className="text-tremor-content hover:text-tremor-content-strong disabled:opacity-30 px-1"
            aria-label="subir"
          >↑</button>
          <button
            type="button"
            onClick={() => move(i, +1)}
            disabled={i === value.length - 1}
            className="text-tremor-content hover:text-tremor-content-strong disabled:opacity-30 px-1"
            aria-label="bajar"
          >↓</button>
          <button
            type="button"
            onClick={() => remove(i)}
            className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 px-1"
            aria-label="quitar"
          >×</button>
        </div>
      ))}
      <Button size="xs" variant="light" onClick={add} disabled={available.length === 0}>
        + Agregar criterio de orden
      </Button>
    </div>
  );
}
